import type { FileImportConfig } from './FileImportSection'
import { TransactionImportDetailsModal } from './TransactionImportDetailsModal'
import { ProjectionImportDetailsModal } from './ProjectionImportDetailsModal'
import { ProjectionImportRowActions } from './ProjectionImportRowActions'
// Keys come from the dependency-free module on purpose: importing them from the
// hook modules pulled the supabase client and the mock banking data into every
// consumer of this config (DCP-298 review). See `@/lib/query-keys`.
import { PROJECTION_KEYS, CASH_POSITION_SUMMARIES_KEY } from '@/lib/query-keys'
import { TRANSACTION_IMPORT_DEPENDENT_KEYS } from '@/lib/file-import-invalidation'
import { TransactionImportRowActions } from './TransactionImportRowActions'

/** Configuration for transaction file imports */
export const transactionImportConfig: FileImportConfig = {
  importType: 'transaction',
  title: 'Upload Transactions File',
  description:
    'Upload a CSV or Excel file with your transactions. Required columns: transaction_date, amount, description, facility, bank_account_name, bank_account_number. Each row must have values in these columns. Other columns are optional.',
  requiredHeaders: [
    'transaction_date',
    'amount',
    'description',
    'facility',
    'bank_account_name',
    'bank_account_number',
  ],
  optionalHeaders: ['category', 'vendor', 'currency', 'type'],
  templateSheetName: 'Transactions',
  templateFileName: 'dcp_transactions_import_template.xlsx',
  // Shared with the import deletion path; see the constant for why these keys.
  invalidateKeys: TRANSACTION_IMPORT_DEPENDENT_KEYS,
  columnFormatHelp: (
    <>
      <strong>transaction_date</strong> — YYYY-MM-DD format.{' '}
      <strong>amount</strong> — decimal number (negative for outflows).{' '}
      <strong>facility</strong> — Display Name or Legal Name of an existing facility.{' '}
      <strong>bank_account_name</strong>, <strong>bank_account_number</strong> — must match existing
      bank account.
    </>
  ),
  DetailsModal: TransactionImportDetailsModal,
  RowActions: TransactionImportRowActions,
}

/** Configuration for projection file imports */
export const projectionImportConfig: FileImportConfig = {
  importType: 'projection',
  title: 'Upload Projections File',
  description:
    'Upload a CSV or Excel file with your projections. Required columns: facility, category, date, description, amount. Each row must have values in these columns. Notes column is optional.',
  requiredHeaders: ['facility', 'category', 'date', 'description', 'amount'],
  optionalHeaders: ['notes'],
  templateSheetName: 'Projections',
  templateFileName: 'dcp_projections_import_template.xlsx',
  // Imported projections feed the Projections table and every Cash Position
  // summary. Mirrors what the manual projection mutations in
  // `useProjections.ts` invalidate after a create/update/delete.
  invalidateKeys: [PROJECTION_KEYS.all, CASH_POSITION_SUMMARIES_KEY],
  columnFormatHelp: (
    <>
      <strong>facility</strong> — Display Name or Legal Name of an existing facility.{' '}
      <strong>category</strong> — must match existing category name.{' '}
      <strong>date</strong> — YYYY-MM-DD format.{' '}
      <strong>description</strong> — free text description.{' '}
      <strong>amount</strong> — decimal number (positive for inflows, negative for outflows).
    </>
  ),
  DetailsModal: ProjectionImportDetailsModal,
  RowActions: ProjectionImportRowActions,
}
